import { Suspense, lazy, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const Scene3D = lazy(() => import("@/components/Scene3D"));

const nameChars = "AEONEUH".split("");

const charVariants = {
  hidden: { y: "120%", opacity: 0 },
  visible: (i) => ({
    y: "0%",
    opacity: 1,
    transition: {
      duration: 0.9,
      delay: 0.06 * i + 0.4,
      ease: [0.76, 0, 0.24, 1],
    },
  }),
};

const taglineVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 1, delay: 1.2, ease: [0.65, 0, 0.35, 1] },
  },
};

const nameText = "AEONEUH";

export default function Hero() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });

  // Name moves UP faster (parallax foreground)
  const nameY = useTransform(scrollYProgress, [0, 1], [0, -180]);
  // 3D AK moves UP slower (parallax background)
  const akY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  // Tagline fades out
  const taglineOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  // Overline fades out
  const overlineOpacity = useTransform(scrollYProgress, [0, 0.3], [1, 0]);
  // Overall section scale
  const sectionScale = useTransform(scrollYProgress, [0, 1], [1, 0.95]);

  return (
    <section
      ref={sectionRef}
      id="top"
      className="relative min-h-screen w-full overflow-hidden hero-ambient"
      data-testid="hero-section"
    >
      {/* 3D scene — AK behind the name, slower parallax */}
      <motion.div
        className="absolute inset-0 z-0"
        style={{ y: akY }}
      >
        <Suspense fallback={<div className="absolute inset-0 bg-[#09090b]" />}>
          <Scene3D />
        </Suspense>
      </motion.div>

      {/* Dark overlay to push AK back */}
      <div className="absolute inset-0 z-[1] bg-[#09090b]/40 pointer-events-none" />

      {/* Grid overlay */}
      <div className="absolute inset-0 z-10 tactical-grid opacity-20 pointer-events-none" />

      {/* Vignette */}
      <div className="absolute inset-0 z-10 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_10%,#09090b_85%)]" />

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-48 z-10 pointer-events-none bg-gradient-to-t from-[#09090b] via-[#09090b]/60 to-transparent" />

      {/* Centered name — the hero, faster parallax */}
      <motion.div
        className="relative z-20 min-h-screen flex flex-col items-center justify-center pointer-events-none px-4"
        style={{ y: nameY, scale: sectionScale }}
      >
        {/* Orange overline */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          style={{ opacity: overlineOpacity }}
          className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#F47B20] mb-6"
        >
          ◈ OPERATIVE CALLSIGN
        </motion.div>

        {/* Giant name with glitch */}
        <div className="hero-name relative select-none">
          {/* Main text */}
          <div className="font-display font-black text-center leading-[0.85] tracking-tight uppercase text-[18vw] sm:text-[16vw] md:text-[14vw] lg:text-[13vw] xl:text-[12vw] relative z-10">
            {nameChars.map((char, i) => (
              <span key={i} className="line-mask inline-block">
                <motion.span
                  variants={charVariants}
                  initial="hidden"
                  animate="visible"
                  custom={i}
                  className="inline-block text-stroke-orange transition-colors duration-300"
                  style={{ WebkitTextStroke: "2px #F47B20", color: "transparent" }}
                >
                  {char}
                </motion.span>
              </span>
            ))}
          </div>
          {/* Glitch clone 1 */}
          <div
            className="glitch-layer-1 absolute inset-0 font-display font-black text-center leading-[0.85] tracking-tight uppercase text-[18vw] sm:text-[16vw] md:text-[14vw] lg:text-[13vw] xl:text-[12vw] opacity-0 pointer-events-none z-20"
            style={{ WebkitTextStroke: "2px rgba(244,123,32,0.4)", color: "transparent" }}
            aria-hidden="true"
          >
            {nameText}
          </div>
          {/* Glitch clone 2 */}
          <div
            className="glitch-layer-2 absolute inset-0 font-display font-black text-center leading-[0.85] tracking-tight uppercase text-[18vw] sm:text-[16vw] md:text-[14vw] lg:text-[13vw] xl:text-[12vw] opacity-0 pointer-events-none z-20"
            style={{ WebkitTextStroke: "2px rgba(217,200,80,0.3)", color: "transparent" }}
            aria-hidden="true"
          >
            {nameText}
          </div>
        </div>

        {/* Tagline */}
        <motion.div
          variants={taglineVariants}
          initial="hidden"
          animate="visible"
          style={{ opacity: taglineOpacity }}
          className="mt-6 md:mt-8 font-mono text-[11px] sm:text-xs uppercase tracking-[0.35em] text-white/50 text-center"
        >
          TACTICAL PORTFOLIO // CS2 // WEB DEV
        </motion.div>

        {/* Decorative corner brackets */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1.5, delay: 1.8 }}
          className="absolute top-[15%] left-[5%] md:left-[10%] font-mono text-[#F47B20] text-lg pointer-events-none"
        >
          ┌
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1.5, delay: 1.8 }}
          className="absolute top-[15%] right-[5%] md:right-[10%] font-mono text-[#F47B20] text-lg pointer-events-none"
        >
          ┐
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1.5, delay: 1.8 }}
          className="absolute bottom-[15%] left-[5%] md:left-[10%] font-mono text-[#F47B20] text-lg pointer-events-none"
        >
          └
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.2 }}
          transition={{ duration: 1.5, delay: 1.8 }}
          className="absolute bottom-[15%] right-[5%] md:right-[10%] font-mono text-[#F47B20] text-lg pointer-events-none"
        >
          ┘
        </motion.div>

        {/* Status + scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 2 }}
          className="absolute bottom-10 left-0 right-0 flex items-center justify-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/40"
        >
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full bg-green-400 pulse-dot" />
              <span>STATUS: ACTIVE</span>
            </div>
            <span className="hidden md:inline">•</span>
            <div className="hidden md:block">
              <span>SCROLL ↓</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
